import { useEffect } from 'react'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Apple, ArrowLeftRight, CreditCard, FileCheck2, FileSpreadsheet,
  Leaf, LogOut, Package, PackageOpen, Scale, Tag, Users, Wallet,
} from 'lucide-react'

import { useAuth } from '@/stores/auth'
import { useCurrentCompany } from '@/lib/useCompany'
import { currentCompanyId } from '@/lib/companyUrl'
import { cn } from '@/lib/utils'
import { adminUrl, adminWindowName, focusWindow } from '@/lib/windows'

const TABS = [
  { path: '/main',             label: '발주서 변환',    Icon: ArrowLeftRight },
  { path: '/main/quote',       label: '견적서 변환',    Icon: FileSpreadsheet },
  { path: '/main/verify',      label: '저장 검증',      Icon: FileCheck2 },
  { path: '/main/partners',    label: '거래처관리',     Icon: Users },
  { path: '/main/items',       label: '품목관리',       Icon: Apple },
  { path: '/main/prices',      label: '거래처별 단가',  Icon: Tag },
  { path: '/main/delivery',    label: '출고처리',       Icon: Package },
  { path: '/main/receiving',   label: '입고처리',       Icon: PackageOpen },
  { path: '/main/collections', label: '수금등록',       Icon: Wallet },
  { path: '/main/payments',    label: '지급등록',       Icon: CreditCard },
  { path: '/main/ledger',      label: '채권채무집계',   Icon: Scale },
] as const

// pathname → 담당 탭 경로 (활성 탭 판정과 동일 규칙).
function tabPathFor(pathname: string): string | null {
  const tab = TABS.find((t) =>
    t.path === '/main'
      ? pathname === '/main' || pathname.startsWith('/main/code-match')
      : pathname.startsWith(t.path),
  )
  return tab ? tab.path : null
}

export function MainShell() {
  const nav = useNavigate()
  const path = useRouterState({ select: (s) => s.location.pathname })
  const { user, logout } = useAuth()
  const company = useCurrentCompany()

  // 이 창에 자기 탭 이름을 부여 → 다른 창의 window.open('', 이름) 이 이 창을
  // 찾아 재사용(중복 창 방지). 회사 선택 후 /main 으로 온 '이름없는' 원래 창이
  // 발주서 변환 창으로 두 개 뜨던 문제 해결. (회사 id 는 URL 에서 즉시 읽음)
  useEffect(() => {
    const tp = tabPathFor(path)
    const cid = currentCompanyId()
    if (tp && cid) window.name = `gf-tab-${cid}-${tp.replace(/\W+/g, '_')}`
  }, [path])

  function doLogout() {
    logout()
    nav({ to: '/login' })
  }

  function switchCompany() {
    // 회사 변경 = '회사 선택' 창으로 이동(열려 있으면 그 창, 없으면 새 창). 이 창은 그대로 둔다 —
    // 이 창을 회사 선택으로 바꾸면 회사 선택 창이 둘이 되던 문제(2026-11-04). 팝업 차단이면 이 창에서.
    if (!focusWindow(adminWindowName('회사'), adminUrl('회사'))) nav({ to: '/company-select' })
  }

  // ── 전역 단축키 ──
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // 모달/입력창 안에서 트리거된 이벤트면 무시 (/ 등이 방해되지 않게)
      const t = e.target as HTMLElement | null

      // Alt + N → 현재 페이지의 "추가" 버튼 클릭
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault()
        const btn = document.querySelector<HTMLButtonElement>('[data-role="add-btn"]')
        btn?.click()
        return
      }

      // "/" → 검색창 focus (입력 중이면 무시)
      if (e.key === '/' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const isTyping = t && (
          t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable
        )
        if (isTyping) return
        const box = document.querySelector<HTMLInputElement>('[data-role="search-box"]')
        if (box) { e.preventDefault(); box.focus(); box.select() }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [nav])

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="w-60 bg-white border-r border-slate-200 flex flex-col no-print">
        {/* 브랜드 */}
        <div className="px-5 py-5 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
              <Leaf className="w-5 h-5" strokeWidth={2.4}/>
            </div>
            <div>
              <div className="font-bold text-base tracking-tight leading-tight">그린푸드</div>
              <div className="text-[10px] text-emerald-100/90 uppercase tracking-widest">Fresh Produce ERP</div>
            </div>
          </div>
        </div>

        {/* 현재 회사 */}
        <div className="px-5 py-3.5 border-b border-slate-100 bg-emerald-50/40">
          <div className="text-[10px] text-emerald-700/70 uppercase tracking-wider font-bold">현재 회사</div>
          <div className="font-semibold text-slate-900 text-sm mt-0.5 truncate">{company?.name || '—'}</div>
          <button onClick={switchCompany}
                  className="text-xs text-emerald-700 hover:text-emerald-900 mt-1 font-medium">
            회사 변경 →
          </button>
        </div>

        {/* 탭 메뉴 — 다른 탭 클릭 시 별도 브라우저 창으로 열어 현재 작업 보존.
            경로별 고정 창 이름을 사용해, 이미 열려있으면 그 창으로 이동·포커스(중복 방지). */}
        <nav className="p-3 flex-1 overflow-y-auto">
          {TABS.map(({ path: p, label, Icon }) => {
            const active = p === '/main'
              ? (path === '/main' || path.startsWith('/main/code-match'))
              : path.startsWith(p)
            return (
              <a
                key={p}
                href={p}
                onClick={(e) => {
                  e.preventDefault()
                  if (active) return
                  // 회사별·경로별 고정 창 이름으로 중복 방지. 새 창은 URL 에
                  // ?company=id 를 넣어 그 창이 회사를 알 수 있게 한다(창별 독립).
                  const cid = company?.id
                  const url = cid ? `${p}?company=${cid}` : p
                  const winName = `gf-tab-${cid ?? 'x'}-${p.replace(/\W+/g, '_')}`
                  // 빈 URL 로 열면: 같은 회사의 기존 창은 '이동 없이' 재사용(리로드 방지, 작업 보존),
                  // 새 창은 about:blank 로 열리므로 그때만 실제 페이지로 이동시킨다.
                  const win = window.open('', winName)
                  if (!win) {
                    // 팝업 차단 등 → 현재 창에서 이동(retainSearchParams 로 ?company 유지).
                    nav({ to: p, search: (prev) => prev })
                    return
                  }
                  if (win.location.href === 'about:blank') win.location.href = url
                  win.focus()
                }}
                className={cn(
                  'flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium',
                  'transition-all mb-0.5 cursor-pointer',
                  active
                    ? 'bg-emerald-100/80 text-emerald-800 shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <Icon className={cn('w-4 h-4 shrink-0', active ? 'text-emerald-700' : 'text-slate-400')}/>
                <span className="truncate flex-1">{label}</span>
              </a>
            )
          })}
        </nav>

        {/* 사용자 */}
        <div className="p-3 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold ring-1 ring-emerald-200">
              {(user?.display_name || user?.username || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-700 truncate">{user?.display_name || user?.username}</div>
            </div>
            <button onClick={doLogout} title="로그아웃"
                    className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors">
              <LogOut className="w-4 h-4"/>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
